import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    FiTruck, FiSearch, FiX, FiUser, FiMapPin, FiCheck,
    FiCheckCircle, FiClock, FiFilter, FiHome, FiLayers
} from 'react-icons/fi';
import { TbTruckDelivery } from 'react-icons/tb';
import { GiWoodBeam } from 'react-icons/gi';
import { BrickWall, Grid2X2 } from 'lucide-react';
import { format } from 'date-fns';
import BackButton from '../common/BackButton';
import { getFinishedDeliveries, FINISHED_DELIVERIES_PAGE_SIZE } from '../../lib/supabase/finishedDeliveries';

// ─── Quote type helpers ───────────────────────────────────────────────────────

const QUOTE_TYPES = ['roof', 'wall', 'floor', 'timber'];

const getQuoteIcon = (type, className = 'w-4 h-4') => {
    switch (type) {
        case 'roof': return <FiHome className={className} />;
        case 'wall': return <BrickWall className={className} />;
        case 'floor': return <Grid2X2 className={className} />;
        case 'timber': return <GiWoodBeam className={className} />;
        default: return <FiLayers className={className} />;
    }
};

const getQuoteColors = (type) => {
    switch (type) {
        case 'roof': return { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' };
        case 'wall': return { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-800' };
        case 'floor': return { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', border: 'border-green-200 dark:border-green-800' };
        case 'timber': return { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' };
        default: return { bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-700 dark:text-gray-300', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-700' };
    }
};

// ─── Virtual table row height ─────────────────────────────────────────────────
const ROW_HEIGHT = 72;

// ─── useInfiniteScroll (same pattern as JobEditorTable) ───────────────────────
const useInfiniteScroll = (callback, hasMore, isLoading, containerRef) => {
    const [isFetching, setIsFetching] = useState(false);

    useEffect(() => {
        if (!isFetching) return;
        const run = async () => {
            await callback();
            setIsFetching(false);
        };
        run();
    }, [isFetching]);

    useEffect(() => {
        const threshold = 400;
        const handleScroll = () => {
            if (isFetching || !hasMore || isLoading) return;
            const el = containerRef?.current;
            if (!el) return;
            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
            if (nearBottom) setIsFetching(true);
        };
        const el = containerRef?.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [isFetching, hasMore, isLoading, containerRef]);

    return [isFetching, setIsFetching];
};

// ─── Delivery Card ────────────────────────────────────────────────────────────
const DeliveryCard = ({ row, onClick }) => {
    const colors = getQuoteColors(row.quoteType);

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try { return format(new Date(dateStr), 'dd MMM yyyy, h:mm a'); }
        catch { return '—'; }
    };

    console.log(row)

    return (
        <div
            className={`finished-card flex items-stretch gap-0 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
            onClick={() => onClick(row.jobId)}
        >
            {/* Left colour strip */}
            <div className={`w-1.5 flex-shrink-0 ${colors.bg} border-r ${colors.border}`} />

            <div className="finished-card-body flex flex-1 flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">

                {/* Quote type badge */}
                <div className={`flex items-center gap-1.5 px-2.5 py-1 min-w-20 rounded-lg text-xs font-semibold ${colors.badge} flex-shrink-0`}>
                    {getQuoteIcon(row.quoteType, 'w-3.5 h-3.5')}
                    <span className="capitalize">{row?.uniqueId || row?.quoteType}</span>
                </div>

                {/* Customer */}
                <div className="flex flex-col min-w-[140px] flex-1">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Customer</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.customerName || '—'}</span>
                </div>

                {/* Address */}
                <div className="flex flex-col min-w-[140px] flex-1 sm:flex">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium flex items-center gap-1">
                        <FiMapPin className="w-3 h-3" /> Address
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{row.customerAddress || '—'}</span>
                </div>

                {/* Invoice status */}
                <div className="flex flex-col items-center flex-shrink-0">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Invoice</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${row.invoiced
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                        {row.invoiced ? <FiCheckCircle className="w-3 h-3" /> : <FiClock className="w-3 h-3" />}
                        {row.invoiced ? 'Invoiced' : 'Pending'}
                    </span>
                </div>

                {/* Quote Sent */}
                <div className="flex flex-col items-center flex-shrink-0">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Quote Sent</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${row.quoteSent
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                        {row.quoteSent ? <FiCheck className="w-3 h-3" /> : <FiX className="w-3 h-3" />}
                        {row.quoteSent ? 'Sent' : 'Not Sent'}
                    </span>
                </div>

                {/* Delivery Status */}
                <div className="flex flex-col items-center flex-shrink-0">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Delivery</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        <TbTruckDelivery className="w-3.5 h-3.5" />
                        Delivered
                    </span>
                </div>

                {/* Driver */}
                <div className="flex flex-col flex-shrink-0">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium flex items-center gap-1">
                        <FiUser className="w-3 h-3" /> Driver
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[120px]">
                        {row.assignedDriverName || '—'}
                    </span>
                </div>

                {/* Delivered at */}
                <div className="flex flex-col flex-shrink-0 ml-auto text-right">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Completed</span>
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">{formatDate(row.deliveredAt)}</span>
                </div>

            </div>
        </div>
    );
};

// ─── Skeleton card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
    <div className="flex items-center gap-4 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 shadow-sm animate-pulse">
        <div className="w-16 h-6 bg-gray-200 dark:bg-zinc-600 rounded-lg" />
        <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-gray-200 dark:bg-zinc-600 rounded w-1/3" />
            <div className="h-4 bg-gray-200 dark:bg-zinc-600 rounded w-1/2" />
        </div>
        <div className="w-20 h-6 bg-gray-200 dark:bg-zinc-600 rounded-full" />
        <div className="w-20 h-6 bg-gray-200 dark:bg-zinc-600 rounded-full" />
        <div className="w-24 h-6 bg-gray-200 dark:bg-zinc-600 rounded-full" />
        <div className="w-24 h-4 bg-gray-200 dark:bg-zinc-600 rounded" />
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const FinishedDeliveries = () => {
    const { company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [quoteTypeFilter, setQuoteTypeFilter] = useState('');

    const containerRef = useRef(null);
    const searchDebounceRef = useRef(null);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchPage = useCallback(async (pageIndex, reset = false) => {
        if (!company?.id) return;
        if (pageIndex === 0) setIsLoading(true);
        else setIsLoadingMore(true);

        try {
            const result = await getFinishedDeliveries(
                company.id,
                pageIndex,
                search,
                quoteTypeFilter
            );
            setRows(prev => reset || pageIndex === 0 ? result.data : [...prev, ...result.data]);
            setTotal(result.total);
            setHasMore(result.hasMore);
            setPage(pageIndex);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [company?.id, search, quoteTypeFilter]);

    // Initial + filter-driven load
    useEffect(() => {
        fetchPage(0, true);
    }, [fetchPage]);

    // ── Infinite scroll ────────────────────────────────────────────────────────
    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore) return;
        await fetchPage(page + 1);
    }, [hasMore, isLoadingMore, page, fetchPage]);

    const [isFetching] = useInfiniteScroll(loadMore, hasMore, isLoadingMore, containerRef);

    // ── Search debounce ────────────────────────────────────────────────────────
    const handleSearchChange = (value) => {
        setSearchInput(value);
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => setSearch(value), 400);
    };

    // ── Virtualizer ────────────────────────────────────────────────────────────
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => ROW_HEIGHT + 8, // card + gap
        overscan: 5,
    });

    const handleCardClick = (jobId) => {
        navigate(`/${companySlug}/assignment/${jobId}`, {
            state: { from: location.pathname }
        });
    };

    // ── Group counts for the filter tabs ──────────────────────────────────────
    const quoteTabLabel = (qt) => qt.charAt(0).toUpperCase() + qt.slice(1);

    return (
        <div className="finished-page flex flex-col h-full">

            {/* ── Header ── identical pattern to QuoteSchedule ── */}
            <div className="finished-header flex flex-col items-start justify-between gap-4 mb-6">
                <BackButton
                    text="Go Back"
                    variant="ghost"
                    size="sm"
                    iconType="chevron"
                    className="text-blue-600 hover:text-blue-800"
                />
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <TbTruckDelivery className="w-8 h-8 text-emerald-600" />
                        Finished Deliveries
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        All completed deliveries, separated by quote department
                    </p>
                </div>
            </div>

            {/* ── Search + Quote-type filter bar ── */}
            <div className="finished-toolbar flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">

                {/* Search */}
                <div className="relative flex-1 max-w-sm">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="Search customer or address…"
                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition"
                    />
                    {searchInput && (
                        <button
                            onClick={() => { setSearchInput(''); setSearch(''); }}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <FiX className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Quote type tabs */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                        onClick={() => setQuoteTypeFilter('')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${quoteTypeFilter === ''
                            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                            : 'bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-600'
                            }`}
                    >
                        All
                    </button>
                    {QUOTE_TYPES.map((qt) => {
                        const colors = getQuoteColors(qt);
                        const isActive = quoteTypeFilter === qt;
                        return (
                            <button
                                key={qt}
                                onClick={() => setQuoteTypeFilter(qt === quoteTypeFilter ? '' : qt)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isActive ? `${colors.badge}` : 'bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-600'
                                    }`}
                            >
                                {getQuoteIcon(qt, 'w-3.5 h-3.5')}
                                {quoteTabLabel(qt)}
                            </button>
                        );
                    })}
                </div>

                {/* Total count */}
                {!isLoading && (
                    <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {total.toLocaleString()} result{total !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* ── Virtual scrollable list ── */}
            <div
                ref={containerRef}
                className="finished-list flex-1 overflow-y-auto overscroll-contain pr-1"
                style={{ minHeight: 0 }}
            >
                {isLoading ? (
                    <div className="space-y-2">
                        {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-zinc-700 flex items-center justify-center mb-4">
                            <TbTruckDelivery className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-900 dark:text-white font-semibold text-lg mb-1">No finished deliveries</p>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            {search || quoteTypeFilter
                                ? 'Try adjusting your search or filters'
                                : 'Completed deliveries will appear here'}
                        </p>
                    </div>
                ) : (
                    <div
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            position: 'relative',
                            width: '100%',
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[virtualRow.index];
                            if (!row) return null;
                            return (
                                <div
                                    key={virtualRow.key}
                                    ref={rowVirtualizer.measureElement}
                                    data-index={virtualRow.index}
                                    style={{
                                        position: 'absolute',
                                        top: virtualRow.start,
                                        left: 0,
                                        right: 0,
                                        minHeight: ROW_HEIGHT + 8,
                                        paddingBottom: 8,
                                    }}
                                >
                                    <DeliveryCard row={row} onClick={handleCardClick} />
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Load-more indicator */}
                {(isLoadingMore || isFetching) && (
                    <div className="flex items-center justify-center py-6 gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Loading more…
                    </div>
                )}

                {!isLoading && !hasMore && rows.length > 0 && (
                    <p className="text-center text-xs text-gray-400 dark:text-gray-600 py-6">
                        All {total.toLocaleString()} deliveries loaded
                    </p>
                )}
            </div>
        </div>
    );
};

export default FinishedDeliveries;
